import React, { useEffect, useState } from 'react';
import { TitleBar } from '../../components/TitleBar';
import InputWithLabel from '../../components/InputWithLabel';
import { useAuth } from '../../hooks/useAuth';
import { plebsRepository } from '../../repositories/PlebsRepository';
import { Button, ButtonSize } from '../../components/Button';
import { ButtonVariant } from '../../config/colors';
import { isValidEmail } from '../../utils/Validation';
import { useConfirm } from '../../hooks/useConfirm';
import { LoginType } from '../../models/LoginType';
import { authRepository, OtpReason } from '../../repositories/AuthRepository';
import EditProfileImages from '../../components/EditProfileImages';

interface FormEditProfileProps {
  
}

interface PlebData {
    name: string;
    bio: string;
    slug: string;
    profession: string;
    website: string;
    city: string;
    interests: string;
    yob: string;
    nostr: string;
    email: string;
    twitterSlug: string;
}

const FormEditProfile: React.FC<FormEditProfileProps> = () => {
    const {pleb, updateCurrentPleb} = useAuth();
    const {openConfirm, openOtpDialog} = useConfirm();
    const [plebData, setPlebData] = useState<PlebData>({
        name: '',
        bio: '',
        slug: '',
        profession: '',
        website: '',
        city: '',
        interests: '',
        yob: '',
        nostr: '',
        email: '',
        twitterSlug: ''
    });

    useEffect(() => {
        if(pleb) {
            setPlebData({
                name: pleb.name,
                bio: pleb.bio ?? '',
                slug: pleb.slug,
                profession: pleb.profession ?? '',
                website: pleb.website ?? '',
                city: pleb.city ?? '',
                interests: pleb.interests ?? '',
                yob: pleb.yob?.toString() ?? '',
                nostr: pleb.nostr ?? '',
                email: pleb.email ?? '',
                twitterSlug: pleb.twitterSlug ?? ''
            });
        }
    }, []);

    const getOldValue = (key: string) => {
        let oldValue = '';

        if(pleb && key in pleb) {
            const value = pleb[key as keyof PlebData];
            if(value) {
                oldValue = value.toString();
            }
        }

        return oldValue;
    }

    const isModified = (key: string) => {
        const oldValue = getOldValue(key);
        const newValue = plebData[key as keyof PlebData];

        return oldValue !== newValue;
    }

    const handleChange = (key: string, value: string) => {
        setPlebData({
            ...plebData,
            [key]: value
        });
    }

    const handleBlur = (key: string) => {
        if(isModified(key)) {
            let newValue: any = plebData[key as keyof PlebData];

            if(['email', 'nostr'].includes(key)) {
                newValue = newValue.trim();

                if(newValue === '') {
                    emptyField(key);
                }

                return;
            }

            if(key === 'yob') {
                newValue = parseInt(newValue);
            }
            if(key === 'city' && newValue === '') {
                newValue = null;
            }

            saveField(key, newValue);
        }
    }

    const saveField = (key: string, newValue: any) => {
        if(key === 'nostr')
            key = 'nostrPublicKey';

        plebsRepository.updateUser({
            [key]: newValue
        })
            .then((pleb) => {
                updateCurrentPleb(pleb)
            })
            .catch((error) => {
                handleChange(key, getOldValue(key));
                openConfirm({
                    title: error.response?.data?.message ?? 'Error while saving the field',
                    buttons: [
                        {
                            label: 'OK',
                            variant: ButtonVariant.primary,
                        }
                    ]
                })
            });
    }

    const emptyField = (key: string) => {
        if(key === 'email' && !plebData.nostr) {
            openConfirm({
                title: 'Error',
                message: 'You must have a npub to empty the email.',
                buttons: [
                    {label: 'OK', onClick: () => {}, variant: ButtonVariant.primary}
                ]
            });
        }
        else if(key === 'nostr' && !plebData.email) {
            openConfirm({
                title: 'Error',
                message: 'You must have an email to empty the npub.',
                buttons: [
                    {label: 'OK', onClick: () => {}, variant: ButtonVariant.primary}
                ]
            });
        }
        else {
            const message = key === 'email' ? 'If you remove your email you will only be able to login with your npub' : 'If you remove your npub you will only be able to login with your email';
            const button = key === 'email' ? 'Remove email' : 'Remove npub';

            openConfirm({
                title: message,
                buttons: [
                    {label: button, onClick: () => {
                        setPlebData({
                            ...plebData,
                            [key]: ''
                        });
                        saveField(key, null);
                    }, variant: ButtonVariant.danger},
                    {label: 'Cancel', onClick: () => {
                        setPlebData({
                            ...plebData,
                            [key]: getOldValue(key)
                        });
                    }, variant: ButtonVariant.transparent},
                ]
            });
        }
    }

    const manualSave = (key: string) => {
        let loginType = key === 'email' ? LoginType.EMAIL : LoginType.NOSTR;
        
        authRepository.sendOtp(loginType, plebData[key as keyof PlebData], OtpReason.UPDATE)
            .then((otpId) => {
                openOtpDialog({
                    loginType: loginType,
                    email: plebData.email,
                    npub: plebData.nostr,
                    otpId: otpId
                });
            })
            .catch((error) => {
                openConfirm({
                    title: error.response?.data?.message ?? 'Error while saving the field',
                    buttons: [
                        {
                            label: 'OK',
                            variant: ButtonVariant.primary,
                        }
                    ]
                });
            });
    }

    const renderElementRight = (key: string) => {
        const newValue = plebData[key as keyof PlebData];
        const isFieldModified = isModified(key);

        if(newValue === '') {
            return null;
        } else if(isFieldModified) {
            let btnSavedDisabled = false;

            if(key === 'email') {
                btnSavedDisabled = !isValidEmail(newValue);
            }
            
            return <Button 
                variant={ButtonVariant.primary} 
                onClick={() => manualSave(key)}
                size={ButtonSize.small}
                className="mx-2"
                disabled={btnSavedDisabled}
            >Save</Button>
        } else {
            return <div 
                onClick={() => emptyField(key)} 
                className="p-2 text-brand-color-3 cursor-pointer flex justify-center items-center"
            >
                <i className="material-icons">cancel</i>
            </div>
        }
    }

    return (
        <div className="flex-1 px-4 pt-8 overflow-hidden flex flex-col gap-8 overflow-y-auto">
            <TitleBar title="Edit Profile" />
            <div className="flex flex-col gap-2">
                <EditProfileImages />
                <InputWithLabel 
                    label="PROFILE NAME" 
                    value={plebData.name} 
                    onChange={(value) => handleChange('name', value)}
                    onBlur={() => handleBlur('name')}
                />
                <InputWithLabel 
                    label="USERNAME" 
                    value={plebData.slug} 
                    onChange={(value) => handleChange('slug', value)}
                    onBlur={() => handleBlur('slug')}
                    elementBeforeInput={<div className="text-brand-color-3">@</div>}
                />
                <InputWithLabel 
                    label="BIO" 
                    value={plebData.bio} 
                    onChange={(value) => handleChange('bio', value)}
                    onBlur={() => handleBlur('bio')}
                    type="textarea" 
                    maxLength={400}
                />
                <InputWithLabel 
                    label="PROFESSION" 
                    value={plebData.profession} 
                    onChange={(value) => handleChange('profession', value)}
                    onBlur={() => handleBlur('profession')}
                    maxLength={100}
                />
                <InputWithLabel 
                    label="WEBSITE" 
                    value={plebData.website} 
                    onChange={(value) => handleChange('website', value)}
                    onBlur={() => handleBlur('website')}
                />
                <InputWithLabel 
                    label="CITY" 
                    value={plebData.city} 
                    onChange={(value) => handleChange('city', value)}
                    onBlur={() => handleBlur('city')}
                    maxLength={100}
                />
                <InputWithLabel 
                    label="INTERESTS" 
                    value={plebData.interests} 
                    onChange={(value) => handleChange('interests', value)}
                    onBlur={() => handleBlur('interests')}
                    maxLength={100}
                />
                <InputWithLabel 
                    label="BIRTH YEAR" 
                    value={plebData.yob} 
                    onChange={(value) => handleChange('yob', value)}
                    onBlur={() => handleBlur('yob')}
                    allowedPattern={/^\d*$/}
                />
            </div>

            <div className="flex flex-col gap-2 mb-4">
                <div className="text-brand-color-3 font-semibold mb-1">SOCIALS</div>
                <InputWithLabel 
                    label="NOSTR NPUB" 
                    value={plebData.nostr} 
                    onChange={(value) => handleChange('nostr', value)}
                    elementRight={renderElementRight('nostr')}
                />
                <InputWithLabel 
                    label="X" 
                    value={plebData.twitterSlug} 
                    onChange={(value) => handleChange('twitterSlug', value)}
                    onBlur={() => handleBlur('twitterSlug')}
                    elementBeforeInput={<div className="text-brand-color-3">@</div>}
                />
                <InputWithLabel 
                    label="EMAIL" 
                    value={plebData.email} 
                    onChange={(value) => handleChange('email', value)}
                    elementRight={renderElementRight('email')}
                />
            </div>
        </div>
    )
};

export default FormEditProfile;
